import { call } from '@advanza/api'
import { InputField } from '@advanza/input'
import { Button, LoadingDots } from '@advanza/ui'
import AutoRow from 'components/misc/AutoRow'
import { useToast } from 'components/misc/Toaster'
import { CreateFreeLeadData, FREE_LEAD_TYPE_BULK_GIVEAWAY } from 'interfaces/FreeLeads'
import React, { useState } from 'react'

const DEFAULT_COMMENT = 'Bulk free leads giveaway'
const DEFAULT_NR_LEADS = 1
const MAX_LEADS = 5
const MIN_LEADS = 1

const GiveAwayFreeLeads = () => {
    const { addToast } = useToast()
    const [isLoading, setIsLoading] = useState(false)
    const [providerIds, setProviderIds] = useState('')
    const [comment, setComment] = useState('')
    const [nrLeads, setNrLeads] = useState(DEFAULT_NR_LEADS)

    const handleSubmit = async () => {
        if (isLoading) {
            return
        }

        if (!providerIds.trim()) {
            addToast({ msg: 'Please enter provider IDs', red: true })
            return
        }

        if (nrLeads <= 0) {
            addToast({ msg: 'Please enter a valid number of leads', red: true })
            return
        }

        setIsLoading(true)

        try {
            // Parse comma-separated provider IDs
            const ids = providerIds
                .split(',')
                .map((id) => id.trim())
                .filter((id) => id && !isNaN(parseInt(id)))
                .map((id) => parseInt(id))

            if (ids.length === 0) {
                addToast({ msg: 'No valid provider IDs found', red: true })
                return
            }

            // Create free leads for each provider
            const promises = ids.map((providerId) => {
                const freeLeadData: CreateFreeLeadData = {
                    service_provider_id: providerId,
                    nr_leads: nrLeads,
                    comment: comment || DEFAULT_COMMENT,
                    free_lead_type: FREE_LEAD_TYPE_BULK_GIVEAWAY,
                }

                return call('office/providers/add-free-leads/', {
                    json: freeLeadData,
                })
            })

            await Promise.all(promises)

            addToast({
                msg: `Successfully gave ${nrLeads} free leads to ${ids.length} provider(s)`,
                green: true,
                icon: 'check_circle',
            })

            // Clear form
            setProviderIds('')
            setComment('')
            setNrLeads(DEFAULT_NR_LEADS)
        } catch (error) {
            console.error('Error giving away free leads:', error)
            addToast({
                msg: 'Error giving away free leads. Please check the provider IDs and try again.',
                red: true,
            })
        } finally {
            setIsLoading(false)
        }
    }

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSubmit()
        }
    }

    const handleProviderIdsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setProviderIds(e.target.value)
    }

    const handleNrLeadsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNrLeads(parseInt(e.target.value) || 0)
    }

    const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setComment(e.target.value)
    }

    return (
        <div>
            <AutoRow>
                <InputField
                    placeholder="Provider IDs (comma separated) e.g.: 123, 456, 789"
                    value={providerIds}
                    onChange={handleProviderIdsChange}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                />
                <InputField
                    placeholder="Number of free leads"
                    type="number"
                    min={MIN_LEADS}
                    max={MAX_LEADS}
                    value={nrLeads}
                    onChange={handleNrLeadsChange}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                />
                <InputField
                    placeholder="Comment for the free leads (optional)"
                    value={comment}
                    onChange={handleCommentChange}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                />
                <Button
                    disabled={isLoading || !providerIds.trim() || !nrLeads}
                    onClick={handleSubmit}>
                    {isLoading ? <LoadingDots /> : 'Give Away Free Leads'}
                </Button>
            </AutoRow>
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                <strong>Note:</strong> This will give {nrLeads} free lead(s) to each provider in the
                list. Make sure all provider IDs are valid before submitting.
            </div>
        </div>
    )
}

export default GiveAwayFreeLeads
